/**
 * The score per locality of the campaign's own territory, read by the
 * accounts' locality, never the commenters' (platform design §11.6): the
 * route picks the scope, so nothing here chooses one. Only a locality with a
 * reading is listed, and a card with none is left out of the page: a row per
 * locality that has nothing to say is a wall of "sem leitura".
 *
 * Also home to the score row, bar and chip the topic, tag and post cards
 * draw with, so every card on the screen paints a score the same way.
 */

import type { ReactNode } from 'react';

import { count, score } from '../../../chrome/measure/format';
import { QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { Chip } from '../../../ui/chip';
import { useLocalities, type Window } from '../api/queries';
import type { LocalityRow } from '../api/schemas';
import { st2, st2Plural } from './messages';
import { SentimentCard } from './SentimentCard';

export function scoreTone(value: number): {
  text: string;
  fill: string;
  chip: 'positive' | 'negative' | 'neutral';
} {
  if (value < 0) return { text: 'text-destructive', fill: 'bg-destructive', chip: 'negative' };
  if (value > 0) return { text: 'text-primary-ink', fill: 'bg-primary', chip: 'positive' };
  return { text: 'text-foreground', fill: 'bg-muted-foreground', chip: 'neutral' };
}

export function ScoreChip(props: { value: number }): ReactNode {
  return (
    <Chip tone={scoreTone(props.value).chip} data-numeric>
      {st2('score.chip', { score: score(props.value, getLocale()) })}
    </Chip>
  );
}

export function commentsLabel(n: number): string {
  return n === 0
    ? st2('comments.none')
    : st2Plural('comments.other', n, { n: count(n, getLocale()) });
}

/** A bar from the centre of the −100..+100 axis to the score, left for
 *  criticism and right for praise; the geometry clamps to the axis. */
function DivergingBar(props: { label: string; value: number }): ReactNode {
  const clamped = Math.min(Math.max(props.value, -100), 100);
  const half = Math.abs(clamped) / 2;
  return (
    <div className="relative h-track w-full rounded-pill bg-secondary">
      <div
        role="img"
        aria-label={`${props.label}: ${score(props.value, getLocale())}`}
        data-slot="bar"
        className={`absolute inset-y-0 rounded-pill ${scoreTone(clamped).fill}`}
        style={{ left: `${clamped < 0 ? 50 - half : 50}%`, width: `${half}%` }}
      />
    </div>
  );
}

/** A list row by default; `as="div"` when the row heads a larger list item. */
export function ScoreRow(props: {
  label: string;
  detail: string;
  value: number | null;
  as?: 'li' | 'div';
}): ReactNode {
  const Row = props.as ?? 'li';
  return (
    <Row
      aria-label={Row === 'li' ? props.label : undefined}
      className="flex items-center gap-3 py-2"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-body-sm truncate">{props.label}</span>
        <span data-numeric className="text-caption text-muted-foreground">
          {props.detail}
        </span>
      </div>
      {props.value === null ? (
        <span className="text-caption text-muted-foreground shrink-0 text-right">
          {st2('score.none')}
        </span>
      ) : (
        <>
          <div className="w-24 shrink-0">
            <DivergingBar label={props.label} value={props.value} />
          </div>
          <span
            data-numeric
            className={`text-body-sm w-16 shrink-0 text-right ${scoreTone(props.value).text}`}
          >
            {score(props.value, getLocale())}
          </span>
        </>
      )}
    </Row>
  );
}

function read(rows: readonly LocalityRow[]): LocalityRow[] {
  return rows.filter((row) => row.score.value !== null);
}

export function RegionView(props: { rows: readonly LocalityRow[] }): ReactNode {
  return (
    <ul className="flex flex-col divide-y">
      {read(props.rows).map((row) => (
        <ScoreRow
          key={row.place_id}
          label={row.name}
          detail={commentsLabel(row.comment_volume.value)}
          value={row.score.value}
        />
      ))}
    </ul>
  );
}

export function RegionCard(props: {
  campaignId: string;
  tenantId: string;
  window: Window;
}): ReactNode {
  const query = useLocalities(props.campaignId, props.tenantId, {
    window: props.window,
    order: 'comment_volume_desc',
  });
  if (query.status === 'success' && read(query.data.localities).length === 0) return null;
  return (
    <SentimentCard title={st2('region.title')}>
      <QueryStates query={query}>{(data) => <RegionView rows={data.localities} />}</QueryStates>
    </SentimentCard>
  );
}
